/**
 * useLocalizedExpertBios Hook
 *
 * Batch-resolves expert bios via the resolve_profiles_for_listing RPC
 * (SECURITY DEFINER, server-side locale resolution + cascade, ≤200 ids per
 * call — chunked transparently).
 *
 * MIGRATION (2026-06-07, profile localization pass Brief 4): previously a
 * strict-locale read of localization_metadata — rows with pending or NULL
 * bodies silently dropped out of the map and cards fell back to the original
 * (often wrong-language) bio. The RPC returns the cascade decision and
 * `display_eligible`, so the map value is always the correct body to render.
 *
 * @example
 * ```tsx
 * const { getBio, isLoading } = useLocalizedExpertBios(expertIds);
 *
 * // In render:
 * <UserCard expert={expert} localizedBio={getBio(expert.id)} />
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { usePrimaryLocale } from './usePrimaryLocale';
import { useLocale } from '@/contexts/LocaleContext';
import { fetchResolvedProfilesForListing, resolveDisplayBody } from '@/lib/profileResolver';

interface LocalizedExpertBiosResult {
  /** Get localized bio for a specific expert ID */
  getBio: (expertId: string) => string | undefined;
  /** Get localized bio with fallback to original bio */
  getBioWithFallback: (expertId: string, originalBio?: string | null) => string | undefined;
  /** Map of expert ID to localized bio */
  biosMap: Map<string, string>;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether localization is needed for current locale */
  needsLocalization: boolean;
}

/**
 * Hook to batch-resolve localized expert bios via the resolver RPC
 *
 * @param expertIds - Array of expert user IDs to fetch bios for
 * @returns Object with getBio helper and loading state
 */
export function useLocalizedExpertBios(expertIds: string[]): LocalizedExpertBiosResult {
  const { settings } = useLocale();
  const { resolveToPrimary } = usePrimaryLocale();

  const contentLocale = resolveToPrimary(settings.locale);
  const needsLocalization = contentLocale !== 'en';

  const { data: biosMap = new Map<string, string>(), isLoading } = useQuery({
    queryKey: ['localized-expert-bios', expertIds, contentLocale],
    queryFn: async () => {
      if (!expertIds.length) return new Map<string, string>();

      const profiles = await fetchResolvedProfilesForListing(expertIds, contentLocale);

      const map = new Map<string, string>();
      for (const p of profiles) {
        // display_eligible decides which body — never no_index (robots only)
        const body = resolveDisplayBody(p);
        if (body) {
          map.set(p.user.id, body);
        }
      }
      return map;
    },
    enabled: expertIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  const getBio = (expertId: string): string | undefined => {
    return biosMap.get(expertId);
  };

  const getBioWithFallback = (expertId: string, originalBio?: string | null): string | undefined => {
    return biosMap.get(expertId) ?? originalBio ?? undefined;
  };

  return {
    getBio,
    getBioWithFallback,
    biosMap,
    isLoading,
    needsLocalization
  };
}
